import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import '../../css/broadcast.css';
import language from '../../utils/LanguageSettings';
import { fetchBroadcastLocation } from '../functions/fetchBroadcastLocation';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import gfm from 'remark-gfm';
import RenderSnA from '../functions/renderSnA';
import { SOURCE_URL } from '../../config';
import 'animate.css';
import { animationClass, animationStyle } from '../../utils/FadeInEffect';
import ReactPaginate from 'react-paginate';
import LocationDistrict from '../../text/broadcast-location-district.json';
import LocationCategory from '../../text/broadcast-location-category.json';

const LocationMember: React.FC<{
  item: any;
  globalIndex: number;
  animationKey: number;
  numberOfResultsPerPage: number;
  category: any;
}> = ({ item, globalIndex, animationKey, numberOfResultsPerPage, category }) => {
  const [animate, setAnimate] = useState(false);
  
  useEffect(() => {
    setAnimate(false);
    const timer = setTimeout(() => {
      setAnimate(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [animationKey, globalIndex]);

  return (
    <div
      key={`${animationKey}-${globalIndex}`}
      className={`col-sm-6 mb-30 location-item ${animationClass(animate, 'in')} ${numberOfResultsPerPage == 0 ? "hide-my-next" : ""}`}
      style={animationStyle(animate, 'in')}
    >
      <div className='h-100 p-4'>
        <h5 className='d-block mb-3 font-medium'>{item?.attributes?.Title}</h5>
        {item?.attributes?.Address && <p className='barlow-font light-gray mb-3'>{item?.attributes?.Address}</p>}
        <div className='mb--3'>
          {item?.attributes?.ContactNo && <a href={'tel:'+item?.attributes?.ContactNo} className='barlow-font d-inline-block light-gray mb-3'>{item?.attributes?.ContactNo}</a>}
          {Array.isArray(item?.attributes?.RestaurantTypes) && item?.attributes?.RestaurantTypes.map((i: any, k: number) => (
            <span className='tag mr-2 d-inline-block color-1 mb-3' key={k}>{getCategoryByLang(category, i)}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

const getCategoryByLang = (category: any, englishId : string) => {

    const processedInput = englishId?.replace(/\s+/g, '').toLowerCase() || '';
    
    const foundItem = category.find((item: { id: string; }) => {
        const processedId = item.id.replace(/\s+/g, '').toLowerCase();
        return processedId === processedInput;
    });
    
    return foundItem ? foundItem.category : null;
}

const BroadcastLocation: React.FC = () => {
  const { '*': rawPath } = useParams<{ '*': string }>();
  const path = rawPath?.replace(/\/$/, '');
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [endOffset, setEndOffset] = useState(itemOffset + itemsPerPage);
  const [currentItems, setCurrentItems] = useState<any>(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingStyle, setLoadingStyle] = useState<string>("none");
  const [loading, setLoading] = useState<boolean>(true);
  const [content, setContent] = useState<any>(null);
  const [members, setMembers] = useState<any>();
  const [originalMembers, setOriginalMembers] = useState<any>();
  const [seo, setSeo] = useState<any>(null);
  const [ga, setGa] = useState<any>(null);
  const [animationKey, setAnimationKey] = useState(0);
  const district = LocationDistrict[language];
  const category = LocationCategory[language];
  const listenerRefDistrict = useRef(null);
  const listenerRefCategory = useRef(null);
  const [isShowDistrict, setIsShowDistrict] = useState<boolean>(false);
  const [isShowCategory, setIsShowCategory] = useState<boolean>(false);
  const [searchItems, setSearchItems] = useState<string[]>([]);
  const [districtItems, setDistrictItems] = useState<string[]>([]);
  const [categoryItems, setCategoryItems] = useState<string[]>([]);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const animationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const searchDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const generateNameToIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    district.forEach((group: { content: any[]; }) => {
      group.content.forEach(item => {
        map[item.district] = item.id;
      });
    });
    category.forEach((item: { category: string | number; id: string; }) => {
      map[item.category] = item.id;
    });
    return map;
  }, [district, category]);

  const generateIdToEnMap = useMemo(() => {
    const map: Record<string, string> = {};
    LocationDistrict.en.forEach(group => {
      group.content.forEach(item => {
        map[item.id] = item.district;
      });
    });
    LocationCategory.en.forEach(item => {
      map[item.id] = item.category;
    });
    return map;
  }, []);

  const getSubDistrictsByAllDistrict = (allDistrictName: string) => {
    const enAllDistrictName = getNameToEnValue(allDistrictName);
    const enGroup = LocationDistrict.en.find(group => 
      group.content.some(item => item.district === enAllDistrictName)
    );
    if (!enGroup) return [];
    return enGroup.content
      .filter(item => !item.district.startsWith('All - '))
      .map(item => item.district);
  };

  const getNameToEnValue = (name: string) => {
    const id = generateNameToIdMap[name] || '';
    return generateIdToEnMap[id] || name;
  };

  const triggerAnimationOnce = () => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
    }
    animationTimerRef.current = setTimeout(() => {
      setAnimationKey(prev => prev + 1);
    }, 150);
  };

  useEffect(() => {
    const fetchData = async () => {
      let data = await fetchBroadcastLocation(language);
      if (data) {
        let locationsData = [];
        if (path == data?.attributes?.Original?.Slug) {
          setContent(data?.attributes?.Original);
          locationsData = data?.attributes?.Original?.Locations?.data || [];
          setSeo(data?.attributes?.Original?.SEO || null);
          setGa(data?.attributes?.Original?.GA || null);
          setItemsPerPage(data?.attributes?.Original?.NoOfRecordsToShowOption1);
          setEndOffset(itemOffset + data?.attributes?.Original?.NoOfRecordsToShowOption1);
        } else if (path == data?.attributes?.Special?.Slug) {
          setContent(data?.attributes?.Special);
          locationsData = data?.attributes?.Special?.Locations?.data || [];
          setSeo(data?.attributes?.Special?.SEO || null);
          setGa(data?.attributes?.Special?.GA || null);
          setItemsPerPage(data?.attributes?.Special?.NoOfRecordsToShowOption1);
          setEndOffset(itemOffset + data?.attributes?.Special?.NoOfRecordsToShowOption1);
        }
        setMembers(locationsData);
        setOriginalMembers(locationsData);
        triggerAnimationOnce();
      }
      setLoading(false);
    };
    fetchData();
  }, [language]);

  useEffect(() => {
    setCurrentItems(members?.slice(itemOffset, endOffset));
    setPageCount(Math.ceil((members?.length || 0) / itemsPerPage));
  }, [members, itemsPerPage, itemOffset]);

  const handlePageClick = (event: { selected: number }) => {
    setLoadingStyle("block");
    setTimeout(() => {
      const selectedPage = event.selected;
      const newOffset = selectedPage * itemsPerPage;
      setItemOffset(newOffset);
      setEndOffset(newOffset + itemsPerPage);
      setCurrentPage(selectedPage);
      setLoadingStyle("none");
      triggerAnimationOnce();
    }, 500);
  };

  const handleOption = (option: number) => {
    setItemsPerPage(option);
    setLoadingStyle("block");
    setTimeout(() => {
      setItemOffset(0);
      setEndOffset(option);
      setCurrentPage(0);
      setCurrentItems(members?.slice(0, option));
      setLoadingStyle("none");
      triggerAnimationOnce();
    }, 500);
  };

  const handleClickDropdown = (dropdown: string) => {
    if (dropdown === "district") {
      setIsShowDistrict(prev => !prev);
    } else if (dropdown === "category") {
      setIsShowCategory(prev => !prev);
    }
  };

  const getCommonDistrictsByGroup = (groupName: string) => {
    const group = district.find((item: { name: string; }) => item.name === groupName);
    if (!group) return [];
    const prefix = getAllPrefix();
    return group.content
      .filter((item: { district: string; }) => !item.district.startsWith(prefix))
      .map((item: { district: any; }) => item.district);
  };

  const getAllDistrictNameByGroup = (groupName: string) => {
    const group = district.find((item: { name: string; }) => item.name === groupName);
    if (!group) return '';
    const prefix = getAllPrefix();
    return group.content.find((item: { district: string; }) => item.district.startsWith(prefix))?.district || '';
  };

  const getAllPrefix = () => {
    return language === 'en' ? 'All - ' : '全部 - ';
  };

  const isAllDistrict = (districtName: string) => {
    const prefix = getAllPrefix();
    return districtName.startsWith(prefix);
  };

  const getAllCategoryLabel = () => {
    const prefix = getAllPrefix();
    return language === 'zh' ? `${prefix}類型` : 'All - Category';
  };

  const getGroupByAllDistrict = (districtName: string) => {
    return district.find((group: { content: any[]; }) =>
      group.content.some(item => item.district === districtName)
    )?.name || '';
  };

  const uniqueArray = (arr: string[]) => {
    const result: string[] = [];
    arr.forEach(item => {
      if (!result.includes(item)) {
        result.push(item);
      }
    });
    return result;
  };

  const handleClickDistrict = (districtName: string) => {
    setDistrictItems(prev => {
      let newDistrictItems = [...prev];
      const prefix = getAllPrefix();

      if (isAllDistrict(districtName)) {
        const groupName = getGroupByAllDistrict(districtName);
        const allDistrictName = districtName;

        if (prev.includes(allDistrictName)) {
          const groupCommonDistricts = getCommonDistrictsByGroup(groupName);
          newDistrictItems = newDistrictItems.filter(
            item => item !== allDistrictName && !groupCommonDistricts.includes(item)
          );
        } else {
          const groupCommonDistricts = getCommonDistrictsByGroup(groupName);
          newDistrictItems = newDistrictItems.filter(
            item => !groupCommonDistricts.includes(item) && !item.startsWith(prefix)
          );
          newDistrictItems.push(allDistrictName);
        }
      } else {
        const groupItem = district.find((group: { content: any[]; }) =>
          group.content.some(item => item.district === districtName)
        );
        const groupName = groupItem?.name || '';
        const allDistrictName = getAllDistrictNameByGroup(groupName);
        const groupCommonDistricts = getCommonDistrictsByGroup(groupName);

        newDistrictItems = newDistrictItems.filter(item => !item.startsWith(prefix) || item !== allDistrictName);

        if (prev.includes(districtName)) {
          newDistrictItems = newDistrictItems.filter(item => item !== districtName);
        } else {
          newDistrictItems.push(districtName);
        }

        const selectedGroupCommon = newDistrictItems.filter(item =>
          groupCommonDistricts.includes(item)
        );
        if (selectedGroupCommon.length === groupCommonDistricts.length) {
          newDistrictItems = newDistrictItems.filter(
            item => !groupCommonDistricts.includes(item)
          );
          newDistrictItems.push(allDistrictName);
        }
      }

      newDistrictItems = uniqueArray(newDistrictItems);

      setSearchItems(prevSearch => {
        const allDistricts = district.flatMap((group: { content: any[]; }) =>
          group.content.map(item => item.district)
        );
        const withoutOldDistrict = prevSearch.filter(item => !allDistricts.includes(item));
        const latestSearchItems = uniqueArray([...withoutOldDistrict, ...newDistrictItems]);
        debounceCallLilterLocations(latestSearchItems, searchInputValue);
        return latestSearchItems;
      });

      return newDistrictItems;
    });
  };
  
  const ALL_COMMON_CATEGORIES = useMemo(() => {
    const allCategoryLabel = getAllCategoryLabel();
    return category
      .filter((item: { category: string; }) => item.category !== allCategoryLabel)
      .map((item: { category: any; }) => item.category);
  }, [category]);
    

  const handleClickCategory = (categoryName: string) => {
    setCategoryItems(prev => {
      let newCategoryItems: string[];
      const allCategoryLabel = getAllCategoryLabel();

      if (categoryName === allCategoryLabel) {
        newCategoryItems = prev.includes(allCategoryLabel) ? [] : [allCategoryLabel];
      } else {
        const withoutAll = prev.filter(item => item !== allCategoryLabel);
        newCategoryItems = withoutAll.includes(categoryName)
          ? withoutAll.filter(item => item !== categoryName)
          : [...withoutAll, categoryName];

        const isAllCommonSelected = ALL_COMMON_CATEGORIES.every((item: string) =>
          newCategoryItems.includes(item)
        );
        if (isAllCommonSelected) {
          newCategoryItems = [allCategoryLabel];
        }
      }

      setSearchItems(prevSearch => {
        const withoutOldCategory = prevSearch.filter(
          item => !ALL_COMMON_CATEGORIES.includes(item) && item !== allCategoryLabel
        );
        const latestSearchItems = uniqueArray([...withoutOldCategory, ...newCategoryItems]);
        debounceCallLilterLocations(latestSearchItems, searchInputValue);
        return latestSearchItems;
      });

      return newCategoryItems;
    });
  };

  const debounceCallLilterLocations = (latestSearchItems: string[], searchInput: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    timerRef.current = setTimeout(() => {
      filterLocations(latestSearchItems, searchInput);
      setLoadingStyle("none");
    }, 800);
  };

  const safeToLowerCase = (value: any) => {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase();
  };

  const filterLocations = (currentSearchItems: string[], searchInput: string) => {
    if (!originalMembers || originalMembers.length === 0) {
      setMembers([]);
      setCurrentItems([]);
      setPageCount(0);
      triggerAnimationOnce();
      return;
    }

    const enSearchValues = currentSearchItems.map(name => getNameToEnValue(name)).filter(Boolean);
    
    const categoryEnList = enSearchValues.filter(val => 
      LocationCategory.en.some(item => item.category === val)
    );
    let districtEnList = enSearchValues.filter(val => 
      LocationDistrict.en.some(group => group.content.some(item => item.district === val))
    );

    const expandedDistrictEnList: string[] = [];
    districtEnList.forEach(val => {
      if (val.startsWith('All - ') && val !== 'All - Category') {
        const subDistricts = getSubDistrictsByAllDistrict(val);
        expandedDistrictEnList.push(...subDistricts);
      } else {
        expandedDistrictEnList.push(val);
      }
    });

    const allCategoryLabel = getAllCategoryLabel();
    const isCategoryAll = categoryEnList.includes('All - Category') || currentSearchItems.includes(allCategoryLabel);
    const uniqueDistrictEnList = [...new Set(expandedDistrictEnList)];

    const searchKeyword = safeToLowerCase(searchInput.trim());
    const filterBySearchInput = (item: any) => {
      if (!searchKeyword) return true;
      
      const attributes = item?.attributes || {};
      const Title = attributes?.Title || '';
      const Address = attributes?.Address || '';
      const ContactNo = attributes?.ContactNo || '';
      const Districts = attributes?.Districts || '';
      const RestaurantTypes = Array.isArray(attributes?.RestaurantTypes) ? attributes.RestaurantTypes : [];

      const combinedFields = [
        safeToLowerCase(Title),
        safeToLowerCase(Address),
        safeToLowerCase(ContactNo),
        safeToLowerCase(Districts),
        ...RestaurantTypes.map((t: any) => safeToLowerCase(t))
      ].join('|');

      return combinedFields.includes(searchKeyword);
    };

    const filtered = originalMembers.filter((item: any) => {
      const attributes = item?.attributes || {};
      const RestaurantTypes = Array.isArray(attributes?.RestaurantTypes) ? attributes.RestaurantTypes : [];
      const Districts = attributes?.Districts || '';

      const categoryMatch = isCategoryAll || 
        categoryEnList.length === 0 || 
        RestaurantTypes.some((type: string) => categoryEnList.includes(type));

      const districtMatch = uniqueDistrictEnList.length === 0 || 
        uniqueDistrictEnList.includes(Districts);

      const searchMatch = filterBySearchInput(item);

      return categoryMatch && districtMatch && searchMatch;
    });

    setMembers(filtered);
    setItemOffset(0);
    setEndOffset(searchKeyword ? content?.NoOfResultsPerPage : content?.NoOfRecordsToShowOption1);
    setItemsPerPage(searchKeyword ? content?.NoOfResultsPerPage : content?.NoOfRecordsToShowOption1);
    setCurrentPage(0);
    triggerAnimationOnce();
    setTimeout(() => {
      if(searchKeyword){
        const target = document.querySelector(".filtered-result.position-relative");
        if (!target) {
          return;
        }

        const targetTop = target.getBoundingClientRect().top + window.scrollY;
        const startTop = window.scrollY;
        const distance = targetTop - startTop;
        let startTime: number | null = null;

        function animate(currentTime: number | null) {
          if (!startTime) startTime = currentTime;
          const timeElapsed = currentTime - startTime;
          const progress = Math.min(timeElapsed / 1000, 1);
          const easeProgress = progress * (2 - progress);

          window.scrollTo(0, startTop + distance * easeProgress);

          if (timeElapsed < 1000) requestAnimationFrame(animate);
        }

        requestAnimationFrame(animate);
      }
    }, 500);
  };

  const handleClickSearchItems = (item: string) => {
    setDistrictItems(prev => prev.filter(i => i !== item));
    setCategoryItems(prev => prev.filter(i => i !== item));
    setSearchItems(prev => {
      const latestSearchItems = uniqueArray(prev.filter(i => i !== item));
      debounceCallLilterLocations(latestSearchItems, searchInputValue);
      return latestSearchItems;
    });
  };

  const handleSearchButtonClick = () => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }
    
    setLoadingStyle("block");
    searchDebounceTimerRef.current = setTimeout(() => {
      debounceCallLilterLocations(searchItems, searchInputValue);
      setIsShowDistrict(false);
      setIsShowCategory(false);
    }, 300);
  };

  useEffect(() => {
    const handleLiener = (event: any) => {
      const targetDiv = document.getElementById('district-content');
      if (targetDiv && !targetDiv.contains(event.target)) {
        setIsShowDistrict(false);
      }
    };

    listenerRefDistrict.current = handleLiener;

    if (isShowDistrict) {
      document.addEventListener('click', handleLiener);
    } else {
      document.removeEventListener('click', handleLiener);
    }

    return () => {
      document.removeEventListener('click', listenerRefDistrict.current);
    };
  }, [isShowDistrict]);

  useEffect(() => {
    const handleLiener = (event: any) => {
      const targetDiv = document.getElementById('category-content');
      if (targetDiv && !targetDiv.contains(event.target)) {
        setIsShowCategory(false);
      }
    };

    listenerRefCategory.current = handleLiener;

    if (isShowCategory) {
      document.addEventListener('click', handleLiener);
    } else {
      document.removeEventListener('click', handleLiener);
    }

    return () => {
      document.removeEventListener('click', listenerRefCategory.current);
    };
  }, [isShowCategory]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
      if (searchDebounceTimerRef.current) clearTimeout(searchDebounceTimerRef.current);
    };
  }, []);

  if (loading) {
    return (
      <>
        <div id="loader">
          <div className="spinner-border"></div>
        </div>
      </>
    );
  }

  if (!content) {
    return <div>No data found</div>;
  }

  const bannerImage = content?.Banner?.BannerImage?.data?.attributes?.url;
  const bannerImageTab = content?.Banner?.BannerImageTab?.data?.attributes?.url ?? bannerImage;
  const bannerImageMob = content?.Banner?.BannerImageMob?.data?.attributes?.url ?? bannerImageTab;

  const bannerImagePosition = content?.Banner?.BannerImagePosition?.toLowerCase();
  const bannerImagePositionTab = content?.Banner?.BannerImagePositionTab?.toLowerCase() ?? bannerImagePosition;
  const bannerImagePositionMob = content?.Banner?.BannerImagePositionMob?.toLowerCase() ?? bannerImagePosition;

  return (
    <>
      <RenderSnA seo={seo} ga={ga} />
      <section className={`broadcast banner_search d-flex align-items-center ${content?.Banner?.BannerTextColor.toLowerCase() == 'light' ? 'black-overlay' : 'white-overlay'}`}>
        <img
          src={SOURCE_URL + bannerImage}
          className="object-desktop-image w-100 img-fit d-xl-block d-none"
          style={{ objectPosition: bannerImagePosition }}
        />
        <img
          src={SOURCE_URL + bannerImageTab}
          className="object-tab-image w-100 img-fit d-xl-none d-lg-block d-none"
          style={{ objectPosition: bannerImagePositionTab }}
        />
        <img
          src={SOURCE_URL + bannerImageMob}
          className="object-mobile-image w-100 img-fit d-lg-none d-block"
          style={{ objectPosition: bannerImagePositionMob }}
        />
        <div className="search_wrap">
          <div className={`mb-5 text-${content?.Banner?.BannerTextPosition?.toLowerCase()} ${content?.Banner?.BannerTextColor.toLowerCase() == 'light' ? 'white-color' : ''}`}>
            <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{content?.Banner?.BannerText}</ReactMarkdown>
          </div>
          <div className="row">
            <div className="col-lg-6 mb-lg-0 mb-sm-4 mb-3 pb-lg-0 pb-sm-2 pb-0">
              <div className="row">
                <div className="col-6 pr-sm-3 pr-2" id='district-content'>
                  <button 
                    type="button" 
                    onClick={() => handleClickDropdown("district")} 
                    className={`location-bordered-input w-100 ${isShowDistrict ? "opened" : ""}`} 
                    data-label={content?.DistrictLabel} 
                    name="district"
                  >
                    {districtItems.length === 0 ? content?.DistrictLabel : districtItems.join(', ')}
                  </button>
                  <div className={`filter-dropdown p-sm-5 p-4 ${isShowDistrict ? "opened" : "invisible"}`} id="district">
                    {Array.isArray(district) && district.map((item) => {
                      const prefix = getAllPrefix();
                      const groupAllDistrict = item.content.find((i: { district: string; }) => i.district.startsWith(prefix))?.district;
                      const isGroupAllSelected = districtItems.includes(groupAllDistrict || '');
                      return (
                        <div key={item.name}>
                          <h5 className="font-weight-bold mb-3">{item.name}</h5>
                          <div className="options-row mb-sm-4 mb-0">
                            {Array.isArray(item.content) && item.content.map((i: any) => {
                              const isNeedInactive = isGroupAllSelected && !i.district.startsWith(prefix);
                              return (
                                <div className="mb-2" key={i.id}>
                                  <input
                                    type="checkbox"
                                    name={`district_name_${i.id}`}
                                    value={i.id}
                                    id={`opt-${i.id}`}
                                    checked={districtItems.includes(i.district)}
                                    onChange={() => handleClickDistrict(i.district)}
                                    className={isNeedInactive ? 'inactive' : ''}
                                    disabled={isNeedInactive}
                                  />
                                  <label htmlFor={`opt-${i.id}`}>{i.district}</label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="col-6 pl-sm-3 pl-2" id='category-content'>
                  <button
                    type="button"
                    onClick={() => handleClickDropdown("category")}
                    className={`location-bordered-input w-100 ${isShowCategory ? "opened" : ""}`}
                    data-label={content?.RestaurantTypeLabel}
                    name="restaurant_type"
                  >
                    {categoryItems.length === 0 ? content?.RestaurantTypeLabel : categoryItems.join(', ')}
                  </button>
                  <div className={`filter-dropdown p-sm-5 p-4 ${isShowCategory ? "opened" : "invisible"}`} id="restaurant-type">
                    <div className="options-row mb-sm-4 mb-0">
                      {Array.isArray(category) && category.map((item) => {
                        const allCategoryLabel = getAllCategoryLabel();
                        const isAllSelected = categoryItems.includes(allCategoryLabel);
                        const isNeedInactive = isAllSelected && item.category !== allCategoryLabel;
                        return <div className="mb-2" key={item.id}>
                          <input
                            type="checkbox"
                            name={`restaurant_name_${item.id}`}
                            value={item.id}
                            id={`opt-${item.id}`}
                            checked={categoryItems.includes(item.category)}
                            onChange={() => handleClickCategory(item.category)}
                            className={isNeedInactive ? 'inactive' : ''}
                            disabled={isNeedInactive}
                          />
                          <label htmlFor={`opt-${item.id}`}>{item.category}</label>
                        </div>
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="row">
                <div className="col-sm-6 mb-sm-0 mb-1">
                  <div style={{ 
                    position: 'relative',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <input 
                      type="text" 
                      name="search_location" 
                      className="location-bordered-input w-100"
                      placeholder={content?.SearchAddressLabel}
                      value={searchInputValue}
                      onChange={(e) => setSearchInputValue(e.target.value)}
                      style={{
                        paddingRight: searchInputValue ? '30px' : '10px',
                        boxSizing: 'border-box',
                        outline: 'none',
                        WebkitAppearance: 'none',
                        appearance: 'none'
                      }}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="col-sm-6 text-sm-left text-center">
                  <button 
                    type="button"
                    className="default-btn w-auto px-5"
                    onClick={handleSearchButtonClick}
                  >
                    {content?.SearchButton}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="search-result">
        <div className="broadcast container container-lg spacer-bottom">
          {currentItems?.length !== 0 ? <div className="filtered-result position-relative">
            <div className="d-lg-block d-none">
              <div className="controls row w-100 align-items-center barlow-font justify-content-between py-4 mb-3 mx-0">
                {searchItems?.length > 0 ? (
                  <>
                    <div className="text-18 col-auto">
                      {content?.SearchButton}:&nbsp;
                    </div>
                    <div className="tags text-18 col">
                      {Array.isArray(searchItems) && searchItems.map((item, index) => (
                        <span
                          className='position-relative d-inline-block pointer-cursor remove-filter'
                          key={`filter-${index}`}
                          onClick={() => handleClickSearchItems(item)}
                        >
                          {item}<i className='icon-cross-icon'></i>
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-18 col-auto"></div>
                )}
                <div className="views text-16 col-auto">
                  {content?.ViewLabel}:&nbsp;
                  <a
                    onClick={() => handleOption(content?.NoOfRecordsToShowOption1)}
                    className={`${itemsPerPage == content?.NoOfRecordsToShowOption1 ? "active" : ""} posts_per_page`}
                  >
                    {content?.NoOfRecordsToShowOption1}
                  </a>
                  &nbsp;|&nbsp;
                  <a
                    onClick={() => handleOption(content?.NoOfRecordsToShowOption2)}
                    className={`${itemsPerPage == content?.NoOfRecordsToShowOption2 ? "active" : ""} posts_per_page`}
                  >
                    {content?.NoOfRecordsToShowOption2}
                  </a>
                </div>
              </div>
            </div>
            <div className='row results-row mt-lg-0 mt-4'>
              <div id="loader" style={{ display: loadingStyle }}>
                <div className="spinner-border"></div>
              </div>
              {Array.isArray(currentItems) && currentItems.map((item: any, index: number) => (
                  <LocationMember
                    key={`${animationKey}-${index}`}
                    item={item}
                    globalIndex={itemOffset + index}
                    animationKey={animationKey}
                    numberOfResultsPerPage={itemsPerPage}
                    category={category}
                  />
                ))}
            </div>
            {itemsPerPage < (members?.length || 0) ? (
              <ReactPaginate
                breakLabel="..."
                nextLabel={currentPage < pageCount - 1 ? <i className="support-icon icon-now-arrow-right"></i> : null}
                onPageChange={handlePageClick}
                pageRangeDisplayed={5}
                pageCount={pageCount}
                previousLabel={currentPage > 0 ? <i className="support-icon icon-now-arrow-left"></i> : null}
                renderOnZeroPageCount={null}
                containerClassName="support-pagination text-center pt-md-5 pt-3 pb-3"
                pageClassName="support-page-item"
                pageLinkClassName="support-page-link"
                activeClassName="active"
                previousClassName="support-page-item"
                nextClassName="support-page-item"
                previousLinkClassName="support-page-link"
                nextLinkClassName="support-page-link"
                disabledClassName="disabled"
                forcePage={currentPage}
              />
            ) : (
              <div className='text-center pt-md-5 pt-3 pb-3'></div>
            )}
            <div className="barlow-font text-center legend-text">
              <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{content?.LegendText}</ReactMarkdown>
            </div>
          </div> :
          <div className="filtered-result position-relative">
            <div id="loader" style={{ display: loadingStyle }}>
                <div className="spinner-border"></div>
              </div>
            <h3 className="text-center font-medium mt-5 pt-sm-5 pt-0">{content?.NoResult.replace("{keyword}",searchInputValue ? searchInputValue : searchItems.join())}</h3>
            <div className="d-flex justify-content-center py-5 no-result-suggestion lg_font_size barlow-font light-gray">
              <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{content?.NoResultSuggestions}</ReactMarkdown>
            </div>
          </div>}
        </div>
      </section>
    </>
  );
};

export default BroadcastLocation;